/**
 * Storedash Filters - Frontend JavaScript
 * Handles AJAX filtering without page reload
 * 
 * 100% Vanilla JavaScript - Zero jQuery dependency
 * Uses native CustomEvent API for widget communication
 * Connects to blazing-fast custom filter tables backend
 *
 * @package Storedash_Checkout
 * @since 3.0.0
 * @updated 2025-10-26 - Converted to vanilla JS, native CustomEvent
 */

(function() {
    'use strict';

    // Per-source request manager: each filter source gets its own AbortController
    // Only cancels the previous request from the SAME source, not from other filters
    if (!window.StoredashRequestManager) {
        window.StoredashRequestManager = {
            controllers: new Map(),

            cancel(source) {
                if (source && this.controllers.has(source)) {
                    this.controllers.get(source).abort();
                    this.controllers.delete(source);
                }
            },

            cancelAll() {
                for (const controller of this.controllers.values()) {
                    controller.abort();
                }
                this.controllers.clear();
            },

            register(controller, source) {
                // Only cancel the previous request from the same source
                this.cancel(source);
                this.controllers.set(source, controller);
            }
        };
    }

    // PERF FIX: Shared resize broadcaster — one window resize listener for all widgets.
    // Widgets subscribe via StoredashResize.on(callback) instead of adding individual listeners.
    // Debounced to 100ms to prevent thrashing.
    if (!window.StoredashResize) {
        window.StoredashResize = {
            _listeners: new Set(),
            _initialized: false,

            on(callback) {
                this._listeners.add(callback);
                if (!this._initialized) {
                    this._initialized = true;
                    let timer;
                    window.addEventListener('resize', () => {
                        clearTimeout(timer);
                        timer = setTimeout(() => {
                            const w = window.innerWidth;
                            const h = window.innerHeight;
                            this._listeners.forEach(fn => fn(w, h));
                        }, 100);
                    });
                }
            },

            off(callback) {
                this._listeners.delete(callback);
            }
        };
    }

    // PERF: Client-side AJAX result cache — LRU Map that caches filter responses.
    // When the user toggles filters back-and-forth, cached results return instantly (0ms).
    // Cleared on sort change or page navigation. Max 50 entries.
    if (!window.StoredashFilterCache) {
        window.StoredashFilterCache = {
            _cache: new Map(),
            _maxSize: 50,

            /**
             * Build a cache key from request parameters.
             * Only includes fields that affect the response.
             */
            _key(params) {
                return JSON.stringify({
                    f: params.filters,
                    c: params.context,
                    p: params.paged,
                    pp: params.per_page,
                    ob: params.orderby,
                    o: params.order,
                    ws: params.widget_settings,
                });
            },

            /**
             * Get cached response or null on miss.
             */
            get(params) {
                const key = this._key(params);
                if (!this._cache.has(key)) return null;
                // LRU: move to end (most recently used)
                const value = this._cache.get(key);
                this._cache.delete(key);
                this._cache.set(key, value);
                return value;
            },

            /**
             * Store response in cache. Evicts oldest entry if at capacity.
             */
            set(params, response) {
                const key = this._key(params);
                // Delete first so re-insert puts it at the end (LRU order)
                if (this._cache.has(key)) {
                    this._cache.delete(key);
                }
                // Evict oldest if at capacity
                if (this._cache.size >= this._maxSize) {
                    const oldest = this._cache.keys().next().value;
                    this._cache.delete(oldest);
                }
                this._cache.set(key, response);
            },

            /**
             * Clear entire cache (on sort change, etc.)
             */
            clear() {
                this._cache.clear();
            },

            get size() {
                return this._cache.size;
            }
        };
    }

    // Client-side descriptor map for instant filter counts
    if (!window.StoredashDescriptorMap) {
        window.StoredashDescriptorMap = {
            _data: null,
            _ready: false,
            _loading: false,
            _sources: null, // preprocessed: { sourceName: { values: [{ slug, productSet }] } }
            _descriptors: null, // preprocessed: { productId: { p, m, r, pop, d, s } }
            _categoryContextSet: null,

            isReady() {
                return this._ready;
            },

            init() {
                if (this._loading || this._ready) return;
                if (typeof storedashFiltersData === 'undefined') return;
                if (!storedashFiltersData.clientSideCounts) return;

                const url = storedashFiltersData.descriptorMapUrl || storedashFiltersData.descriptorFallbackUrl;
                if (!url) return;

                this._loading = true;
                fetch(url)
                    .then(r => {
                        if (!r.ok) throw new Error('HTTP ' + r.status);
                        return r.json();
                    })
                    .then(data => {
                        this._data = data;
                        this._preprocess();
                        this._ready = true;
                        this._loading = false;
                    })
                    .catch(() => {
                        this._loading = false;
                        // Graceful degradation: server-side counts still work
                    });
            },

            _preprocess() {
                if (!this._data || !this._data.sources) return;
                this._sources = {};
                for (const [sourceName, source] of Object.entries(this._data.sources)) {
                    this._sources[sourceName] = {
                        label: source.label,
                        type: source.type,
                        values: (source.values || []).map(v => ({
                            id: v.id,
                            label: v.label,
                            slug: v.slug,
                            v: v.v,
                            productSet: new Set(v.product_ids),
                        })),
                    };
                }
                // Build category context set from product_cat source
                const catSource = this._sources['product_cat'];
                if (catSource) {
                    this._categoryContextSet = new Map();
                    for (const val of catSource.values) {
                        this._categoryContextSet.set(val.slug, val.productSet);
                    }
                }

                // PERF: Preprocess per-product descriptors for client-side sorting
                if (this._data.descriptors) {
                    this._descriptors = {};
                    for (const [pid, desc] of Object.entries(this._data.descriptors)) {
                        this._descriptors[pid] = desc;
                    }
                }
            },

            /**
             * Calculate filter counts client-side using exclude-self pattern.
             * For each source, intersect all OTHER active filter product sets,
             * then count per-value intersection with that result.
             *
             * @param {Object} activeFilters - { sourceName: [slug1, slug2, ...] }
             * @param {Set|null} contextProductIds - category/search scoped product IDs
             * @returns {Object} filter_counts in same format as server: { sourceName: { slug: { count, value_id } } }
             */
            calculateCounts(activeFilters, contextProductIds) {
                if (!this._ready || !this._sources) return null;

                const activeSources = Object.keys(activeFilters || {}).filter(k => {
                    const v = activeFilters[k];
                    if (Array.isArray(v)) return v.length > 0;
                    if (v && typeof v === 'object' && v.values) return v.values.length > 0;
                    return false;
                });

                // Build per-source product sets for active filters
                const sourceProductSets = {};
                for (const sourceName of activeSources) {
                    const filterVals = activeFilters[sourceName];
                    const slugs = Array.isArray(filterVals) ? filterVals : (filterVals.values || []);
                    const sourceData = this._sources[sourceName];
                    if (!sourceData) continue;

                    // Union of all selected values' product sets for this source
                    const unionSet = new Set();
                    for (const val of sourceData.values) {
                        if (slugs.includes(val.slug)) {
                            for (const pid of val.productSet) {
                                unionSet.add(pid);
                            }
                        }
                    }
                    sourceProductSets[sourceName] = unionSet;
                }

                const counts = {};

                for (const [sourceName, sourceData] of Object.entries(this._sources)) {
                    counts[sourceName] = {};

                    // Exclude-self: intersect all OTHER active sources
                    let baseSet = null;
                    for (const otherSource of activeSources) {
                        if (otherSource === sourceName) continue;
                        const otherSet = sourceProductSets[otherSource];
                        if (!otherSet) continue;
                        if (baseSet === null) {
                            baseSet = new Set(otherSet);
                        } else {
                            for (const pid of baseSet) {
                                if (!otherSet.has(pid)) baseSet.delete(pid);
                            }
                        }
                    }

                    // Apply category context if present
                    if (contextProductIds && contextProductIds.size > 0) {
                        if (baseSet === null) {
                            baseSet = new Set(contextProductIds);
                        } else {
                            for (const pid of baseSet) {
                                if (!contextProductIds.has(pid)) baseSet.delete(pid);
                            }
                        }
                    }

                    for (const val of sourceData.values) {
                        let count;
                        if (baseSet === null) {
                            count = val.productSet.size;
                        } else {
                            count = 0;
                            for (const pid of val.productSet) {
                                if (baseSet.has(pid)) count++;
                            }
                        }
                        counts[sourceName][val.slug] = {
                            count: count,
                            value_id: val.id,
                        };
                    }
                }

                return counts;
            },

            /**
             * Calculate the filtered product ID set client-side.
             * Uses the same intersection logic as calculateCounts() but returns
             * the resulting product IDs instead of per-value counts.
             *
             * @param {Object} activeFilters - { sourceName: [slug1, slug2, ...] }
             * @param {Set|null} contextProductIds - category/search scoped product IDs
             * @returns {Set|null} Set of matching product IDs, or null if not ready
             */
            calculateProductIds(activeFilters, contextProductIds) {
                if (!this._ready || !this._sources) return null;

                const activeSources = Object.keys(activeFilters || {}).filter(k => {
                    const v = activeFilters[k];
                    if (Array.isArray(v)) return v.length > 0;
                    if (v && typeof v === 'object' && v.values) return v.values.length > 0;
                    return false;
                });

                // No active filters — return context set (or null for "all products")
                if (activeSources.length === 0) {
                    return contextProductIds || null;
                }

                // Build per-source product sets (union within each source)
                const sourceProductSets = {};
                for (const sourceName of activeSources) {
                    const filterVals = activeFilters[sourceName];
                    const slugs = Array.isArray(filterVals) ? filterVals : (filterVals.values || []);
                    const sourceData = this._sources[sourceName];
                    if (!sourceData) continue;

                    const unionSet = new Set();
                    for (const val of sourceData.values) {
                        if (slugs.includes(val.slug)) {
                            for (const pid of val.productSet) {
                                unionSet.add(pid);
                            }
                        }
                    }
                    sourceProductSets[sourceName] = unionSet;
                }

                // Intersect across all active sources
                let resultSet = null;
                for (const sourceName of activeSources) {
                    const sourceSet = sourceProductSets[sourceName];
                    if (!sourceSet) continue;
                    if (resultSet === null) {
                        resultSet = new Set(sourceSet);
                    } else {
                        for (const pid of resultSet) {
                            if (!sourceSet.has(pid)) resultSet.delete(pid);
                        }
                    }
                }

                // Apply category/search context
                if (contextProductIds && contextProductIds.size > 0 && resultSet) {
                    for (const pid of resultSet) {
                        if (!contextProductIds.has(pid)) resultSet.delete(pid);
                    }
                } else if (contextProductIds && contextProductIds.size > 0 && !resultSet) {
                    resultSet = new Set(contextProductIds);
                }

                return resultSet;
            },

            /**
             * Get category context product IDs as a Set.
             */
            getCategoryContext(categorySlug) {
                if (!this._categoryContextSet || !categorySlug) return null;
                return this._categoryContextSet.get(categorySlug) || null;
            },

            /**
             * Check if client-side sorting is available.
             */
            canSort() {
                return this._ready && this._descriptors && Object.keys(this._descriptors).length > 0;
            },

            /**
             * Sort product IDs client-side using descriptor data.
             * Returns null if sorting is not possible (missing data or unsupported orderby).
             *
             * @param {number[]} productIds - Array of product IDs to sort
             * @param {string} orderby - Sort field: price, date, popularity, rating, menu_order
             * @param {string} order - ASC or DESC
             * @param {boolean} pushOosBottom - Push out-of-stock to bottom
             * @returns {number[]|null} Sorted array, or null if sort not possible client-side
             */
            sortProductIds(productIds, orderby, order, pushOosBottom) {
                if (!this.canSort()) return null;

                // Map orderby to descriptor keys
                const keyMap = {
                    'price': 'p',
                    'price-desc': 'p',
                    'date': 'd',
                    'popularity': 'pop',
                    'total_sales': 'pop', // WC_Query alias for popularity
                    'rating': 'r',
                    'menu_order': 'm',
                };

                const key = keyMap[orderby];
                if (!key) return null; // title sort not supported client-side

                // Determine actual order
                let ascending = (order || 'ASC').toUpperCase() === 'ASC';
                if (orderby === 'price-desc') ascending = false;
                // Default desc for date/popularity/rating
                if (['date', 'popularity', 'rating'].includes(orderby) && !order) ascending = false;

                const descs = this._descriptors;
                const sorted = [...productIds];

                sorted.sort((a, b) => {
                    const da = descs[a];
                    const db = descs[b];

                    // Products without descriptors go to the end
                    if (!da && !db) return 0;
                    if (!da) return 1;
                    if (!db) return -1;

                    // Push OOS bottom if enabled
                    if (pushOosBottom) {
                        if (da.s !== db.s) return db.s - da.s; // instock (1) before OOS (0)
                    }

                    const va = da[key] || 0;
                    const vb = db[key] || 0;

                    if (va !== vb) {
                        return ascending ? (va - vb) : (vb - va);
                    }

                    // Secondary sort: menu_order ASC, then product_id ASC
                    if (key !== 'm') {
                        const ma = da.m || 0;
                        const mb = db.m || 0;
                        if (ma !== mb) return ma - mb;
                    }
                    return a - b;
                });

                return sorted;
            }
        };
    }

    if (typeof storedashFiltersData === 'undefined') {
        return;
    }

    // Initialize descriptor map fetch as early as possible
    window.StoredashDescriptorMap.init();

    class StoredashFilters {
        constructor(container) {
            if (typeof storedashFiltersData === 'undefined') {
                console.warn('Storedash: Filter data not loaded');
                return;
            }

            this.container = container;
            this.source = container.dataset.source;
            this.filters = this.parseURL();
            this.isFiltering = false;
            this.hideEmpty = container.dataset.hideEmpty === 'yes';
            this.debounceTimer = null;
            this._boundHandlers = {};

            // BULLETPROOF FIX: Request queue pattern to prevent race conditions
            this.pendingFilters = null;  // Store filter state if request in progress
            this.controller = null;       // AbortController to cancel superseded requests
            this.lastRequestId = 0;       // Track request sequence

            // Store instance reference on container for cross-widget access
            container._storedashFilterInstance = this;

            this.init();
        }

        init() {
            this.bindEvents();
            this.updateActiveStates();
            this.bindPopoverCloseEvents();
            this.bindCrossWidgetSync();
            this.syncStateFromURL();
            this.captureBaselineCounts();
        }

        /**
         * BULLETPROOF FIX: Sync UI state from URL on page load
         * Ensures bookmarked URLs restore full UI state (sort dropdown, pagination display)
         */
        syncStateFromURL() {
            const params = new URLSearchParams(window.location.search);

            // Sync sort dropdown if orderby/order in URL
            const orderby = params.get('orderby');
            const order = params.get('order');
            if (orderby && order) {
                const sortDropdown = document.querySelector('.storedash-sort-by__dropdown');
                if (sortDropdown) {
                    const value = `${orderby}|${order.toLowerCase()}`;
                    // Check if this value exists in dropdown
                    const option = Array.from(sortDropdown.options).find(opt => opt.value === value);
                    if (option) {
                        sortDropdown.value = value;
                    }
                }
            }

            // Sync pagination display if paged in URL
            const paged = parseInt(params.get('paged')) || 1;
            if (paged > 1) {
                const paginationWrappers = document.querySelectorAll('.storedash-pagination-wrapper');
                paginationWrappers.forEach(wrapper => {
                    wrapper.setAttribute('data-current-page', paged);
                });
            }
        }

        /**
         * BULLETPROOF FIX: Sync this widget with other filter widgets
         * When any filter widget changes, all widgets update their checkboxes
         */
        bindCrossWidgetSync() {
            this._boundHandlers.crossWidgetSync = (event) => {
                const detail = event.detail || {};
                const isCleared = detail.cleared || (detail.filters && Object.keys(detail.filters).length === 0);

                if (isCleared) {
                    // Filters were cleared — reset state and restore visibility of all items
                    this.filters = {};
                    this.updateActiveStates();
                    this.resetFilterItemVisibility();
                    if (detail.filter_counts && Object.keys(detail.filter_counts).length > 0) {
                        this.updateFilterCounts(detail.filter_counts, { cleared: true });
                    } else {
                        this.restoreBaselineCounts();
                    }
                } else if (detail.filters && detail.filters !== this.filters) {
                    // Normal filter change from another widget — sync state
                    this.filters = detail.filters;
                    this.updateActiveStates();
                }
            };
            document.addEventListener('storedash:filters:changed', this._boundHandlers.crossWidgetSync);
        }

        /**
         * Get current page context
         * Reads server-injected context from data-page-context attribute (authoritative).
         * Falls back to body class sniffing for backward compatibility.
         */
        getContext() {
            // Priority 1: Server-injected context (authoritative, works on all Elementor templates)
            const wrapper = document.querySelector('.storedash-products-wrapper[data-page-context]');
            if (wrapper) {
                try {
                    const ctx = JSON.parse(wrapper.dataset.pageContext);
                    return {
                        is_shop: ctx.is_shop || false,
                        is_category: ctx.is_category || false,
                        is_taxonomy: ctx.is_taxonomy || false,
                        is_search: ctx.is_search || false,
                        category_id: ctx.category_id || 0,
                        taxonomy: ctx.taxonomy || '',
                        term_id: ctx.term_id || 0,
                        search: ctx.search || '',
                    };
                } catch (e) { /* fall through to body class sniffing */ }
            }

            // Priority 2: Body class sniffing (legacy fallback)
            const body = document.body;
            const urlParams = new URLSearchParams(window.location.search);
            const context = {
                is_shop: body.classList.contains('post-type-archive-product') || body.classList.contains('woocommerce-shop'),
                is_category: body.classList.contains('tax-product_cat'),
                is_taxonomy: body.classList.contains('tax-product_tag') || body.classList.contains('woocommerce-taxonomy'),
                is_search: body.classList.contains('search-results') || urlParams.has('s'),
                category_id: 0,
                taxonomy: '',
                term_id: 0,
                search: urlParams.get('s') || '',
            };

            if (context.is_category) {
                const classes = body.className.split(' ');
                for (let className of classes) {
                    if (className.startsWith('term-')) {
                        const termId = className.replace('term-', '');
                        if (!isNaN(termId)) {
                            context.category_id = parseInt(termId);
                            context.term_id = parseInt(termId);
                            context.taxonomy = 'product_cat';
                            break;
                        }
                    }
                }
            }

            if (context.is_taxonomy && !context.is_category) {
                const classes = body.className.split(' ');
                for (let className of classes) {
                    if (className.startsWith('term-')) {
                        const termId = className.replace('term-', '');
                        if (!isNaN(termId)) {
                            context.term_id = parseInt(termId);
                        }
                    }
                    if (className.startsWith('tax-')) {
                        context.taxonomy = className.replace('tax-', '');
                    }
                }
            }

            return context;
        }

        /**
         * Get product grid widget settings for consistent rendering
         * CRITICAL FIX: Scope to product grid widget (not category grid!)
         */
        getProductGridSettings() {
            // PERF FIX: Cache parsed settings to avoid repeated JSON.parse
            if (this._cachedGridSettings) {
                return this._cachedGridSettings;
            }

            // Find product grid widget first (not category grid!)
            const productGridWidget = document.querySelector('.elementor-widget-storedash-product-grid');
            const productsWrapper = productGridWidget ?
                productGridWidget.querySelector('.storedash-products-wrapper[data-settings]') :
                document.querySelector('.storedash-products-wrapper[data-settings]'); // Fallback

            const defaults = {
                show_image: 'yes',
                show_title: 'yes',
                show_price: 'yes',
                show_add_to_cart: 'yes',
                show_sale_badge: 'yes',
                sale_badge_content_type: 'text',
                image_size: 'woocommerce_thumbnail',
                button_icon_position: 'left',
            };

            if (!productsWrapper) {
                this._cachedGridSettings = defaults;
                return defaults;
            }

            // Get settings from data attribute
            const settingsData = productsWrapper.dataset.settings;
            if (settingsData) {
                try {
                    this._cachedGridSettings = JSON.parse(settingsData);
                    return this._cachedGridSettings;
                } catch (error) {
                    console.warn('Storedash: Failed to parse widget settings', error.message);
                }
            }

            this._cachedGridSettings = defaults;
            return defaults;
        }

        bindEvents() {
            // Checkbox/Radio changes
            this._boundHandlers.change = (e) => {
                const input = e.target;
                if (input.type === 'checkbox' || input.type === 'radio') {
                    this.handleInputChange(e);
                }

                // Dropdown select changes
                if (input.classList.contains('storedash-filter-dropdown')) {
                    this.handleDropdownChange(e);
                }
            };
            this.container.addEventListener('change', this._boundHandlers.change);

            // Button clicks
            this._boundHandlers.click = (e) => {
                // Popover button toggle (new pattern) - check this first
                // Skip "Show all filters" button — handled by predefined-filters.js
                const popoverBtn = e.target.closest('.storedash-filter-popover-button');
                if (popoverBtn && !popoverBtn.classList.contains('storedash-show-all-filters-btn')) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handlePopoverToggle(popoverBtn);
                    return;
                }

                const button = e.target.closest('.storedash-filter-button');
                if (button) {
                    e.preventDefault();
                    this.handleButtonClick(button);
                    return;
                }

                // Swatch clicks
                const swatch = e.target.closest('.storedash-filter-swatch');
                if (swatch) {
                    e.preventDefault();
                    this.handleSwatchClick(swatch);
                    return;
                }

                // Collapsible toggle. The whole header row is a hit target, not just
                // the chevron — the chevron alone is a ~16px target and users kept
                // missing it. Reached via .closest() so clicks on the title, the
                // chevron, or the empty space between them all land here.
                //
                // Skipped entirely once the filter has been moved into the mobile
                // panel: predefined-filters.js binds its own animated accordion to the
                // header there and this listener sits on an ancestor, so both would
                // fire on the same click and cancel each other out. (Chevron clicks in
                // the panel are already stopImmediatePropagation'd at capture.)
                const inMobilePanel = !!this.container.closest('.storedash-pf-panel__content');

                const toggle = e.target.closest('.storedash-filter-toggle, .storedash-filter-header');
                if (toggle && !inMobilePanel) {
                    e.preventDefault();
                    this.setCollapsed(!this.container.classList.contains('collapsed'));
                    return;
                }

                // While collapsed, a click anywhere in the card expands it — the card
                // is little more than a header plus padding in that state, so the
                // padding should not be dead space. Deliberately one-way: once open,
                // only the header collapses it again, otherwise clicking the gaps
                // between value rows would slam the list shut mid-selection.
                // ...but never swallow a click aimed at a real control (the search
                // box stays visible while collapsed), or preventDefault() here would
                // stop the input from taking focus.
                if (!inMobilePanel &&
                    this.container.classList.contains('collapsible') &&
                    this.container.classList.contains('collapsed') &&
                    !e.target.closest('input, select, textarea, button, a, label')) {
                    e.preventDefault();
                    this.setCollapsed(false);
                    return;
                }

                // Legacy collapsible button toggle (backward compatibility)
                const collapsibleBtn = e.target.closest('.storedash-filter-collapsible-button');
                if (collapsibleBtn) {
                    e.preventDefault();
                    this.handleCollapsibleToggle(collapsibleBtn);
                    return;
                }

                // Show more/less
                const showMoreBtn = e.target.closest('.storedash-filter-show-more');
                if (showMoreBtn) {
                    e.preventDefault();
                    this.handleShowMore(showMoreBtn);
                    return;
                }
            };
            this.container.addEventListener('click', this._boundHandlers.click);

            // Search
            let searchTimeout;
            this._boundHandlers.input = (e) => {
                const searchInput = e.target.closest('.storedash-filter-search-input');
                if (searchInput) {
                    clearTimeout(searchTimeout);
                    searchTimeout = setTimeout(() => {
                        this.handleSearch(searchInput);
                    }, 300);
                }
            };
            this.container.addEventListener('input', this._boundHandlers.input);
        }

        bindPopoverCloseEvents() {
            // PERF FIX: Singleton pattern — register global popover close/escape listeners once,
            // not per-instance. With 6 filter widgets, this eliminates 5 redundant document listeners.
            if (!window._storedashPopoverCloseRegistered) {
                window._storedashPopoverCloseRegistered = true;

                // Close popover when clicking outside
                document.addEventListener('click', (e) => {
                    const clickedInsidePopover = e.target.closest('.storedash-popover-filter-wrapper');
                    if (!clickedInsidePopover) {
                        document.querySelectorAll('.storedash-filter-popover.active').forEach(popover => {
                            popover.classList.remove('active');
                            const button = popover.parentElement.querySelector('.storedash-filter-popover-button');
                            if (button) {
                                button.setAttribute('aria-expanded', 'false');
                            }
                        });
                    }
                });

                // Close popover on Escape key
                document.addEventListener('keydown', (e) => {
                    if (e.key === 'Escape') {
                        document.querySelectorAll('.storedash-filter-popover.active').forEach(popover => {
                            popover.classList.remove('active');
                            const button = popover.parentElement.querySelector('.storedash-filter-popover-button');
                            if (button) {
                                button.setAttribute('aria-expanded', 'false');
                            }
                        });
                    }
                });
            }
        }

        handleInputChange(e) {
            const input = e.target;
            const value = input.value;
            const type = input.type;

            if (type === 'checkbox') {
                this.toggleFilterValue(value, input.checked);
            } else if (type === 'radio') {
                this.setFilterValue(value);
            }

            this.applyFilters();
        }

        handleButtonClick(button) {
            const value = button.dataset.value;

            button.classList.toggle('active');
            this.toggleFilterValue(value, button.classList.contains('active'));
            this.applyFilters();
        }

        handleSwatchClick(swatch) {
            const value = swatch.dataset.value;

            swatch.classList.toggle('active');
            this.toggleFilterValue(value, swatch.classList.contains('active'));
            this.applyFilters();
        }

        handleDropdownChange(e) {
            const dropdown = e.target;
            const selectedOptions = Array.from(dropdown.selectedOptions).map(option => option.value);
            
            // Clear existing filters for this source
            this.filters[this.source] = selectedOptions.filter(val => val !== '');
            
            if (this.filters[this.source].length === 0) {
                delete this.filters[this.source];
            }
            
            this.applyFilters();
        }

        handlePopoverToggle(button) {
            const wrapper = button.closest('.storedash-popover-filter-wrapper');
            if (!wrapper) {
                return;
            }
            const popover = wrapper.querySelector('.storedash-filter-popover');
            if (!popover) {
                return;
            }
            const isOpen = popover.classList.contains('active');
            
            if (isOpen) {
                // Close popover
                popover.classList.remove('active');
                button.setAttribute('aria-expanded', 'false');
            } else {
                // Close other open popovers first
                document.querySelectorAll('.storedash-filter-popover.active').forEach(p => {
                    p.classList.remove('active');
                    const otherButton = p.parentElement.querySelector('.storedash-filter-popover-button');
                    if (otherButton) {
                        otherButton.setAttribute('aria-expanded', 'false');
                    }
                });
                
                // Open this popover
                popover.classList.add('active');
                button.setAttribute('aria-expanded', 'true');
            }
        }

        /**
         * Collapse/expand this filter card and keep the chevron's aria-expanded
         * in sync. Single entry point so every hit target (chevron, header row,
         * collapsed card body) reports the same state to assistive tech.
         */
        setCollapsed(collapsed) {
            this.container.classList.toggle('collapsed', collapsed);

            const toggleBtn = this.container.querySelector('.storedash-filter-toggle');
            if (toggleBtn) {
                toggleBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
            }
        }

        handleCollapsibleToggle(button) {
            const wrapper = button.closest('.storedash-collapsible-button-wrapper');
            const content = wrapper.querySelector('.storedash-collapsible-content');
            const isCollapsed = wrapper.classList.contains('collapsed');
            
            if (isCollapsed) {
                // Expand
                wrapper.classList.remove('collapsed');
                button.setAttribute('aria-expanded', 'true');
                
                // Smooth expand animation
                content.style.display = 'block';
                content.style.maxHeight = '0';
                content.style.overflow = 'hidden';
                content.style.transition = 'max-height 0.3s ease';
                
                // Trigger reflow
                content.offsetHeight;
                
                // Expand to full height
                content.style.maxHeight = content.scrollHeight + 'px';
                
                // Remove inline styles after animation
                setTimeout(() => {
                    content.style.maxHeight = '';
                    content.style.overflow = '';
                    content.style.transition = '';
                }, 300);
            } else {
                // Collapse
                const currentHeight = content.scrollHeight;
                content.style.maxHeight = currentHeight + 'px';
                content.style.overflow = 'hidden';
                content.style.transition = 'max-height 0.3s ease';
                
                // Trigger reflow
                content.offsetHeight;
                
                // Collapse
                content.style.maxHeight = '0';
                
                setTimeout(() => {
                    wrapper.classList.add('collapsed');
                    button.setAttribute('aria-expanded', 'false');
                    content.style.display = 'none';
                    content.style.maxHeight = '';
                    content.style.overflow = '';
                    content.style.transition = '';
                }, 300);
            }
        }

        handleShowMore(button) {
            // Uses a dedicated `storedash-collapsed` class (NOT `hidden`, which the
            // count-update logic toggles for empty values) so collapsed items survive
            // filter interactions instead of being revealed.
            const collapsedItems = this.container.querySelectorAll('.storedash-filter-item.storedash-collapsed');

            // Per-widget labels come from the button's data attributes (set by
            // FiltersRenderer); the site-wide i18n strings are only the fallback for
            // buttons rendered before those attributes existed.
            const moreText = button.dataset.moreText || storedashFiltersData.i18n.showMore || 'Show More';
            const lessText = button.dataset.lessText || storedashFiltersData.i18n.showLess || 'Show Less';

            if (collapsedItems.length) {
                collapsedItems.forEach(item => item.classList.remove('storedash-collapsed'));
                button.textContent = lessText;
                // Marks the list as expanded so the count-update pass knows this button
                // is currently a "Show Less" and must stay visible even when nothing is
                // collapse-hidden — otherwise the user is stranded in the open state.
                button.classList.add('is-expanded');
            } else {
                // Re-collapse. Only items actually on screen count toward the limit —
                // ones hidden as unavailable must not consume a slot, or the visible
                // list comes back shorter than the limit. Limit comes from the button
                // (the standalone Filters widget lets it be configured); 5 is the
                // fallback for markup rendered before data-limit existed.
                const limit = parseInt(button.dataset.limit, 10) || 5;
                const visibleItems = this.container.querySelectorAll('.storedash-filter-item:not(.hidden)');

                let collapsedCount = 0;
                visibleItems.forEach((item, index) => {
                    if (index >= limit) {
                        item.classList.add('storedash-collapsed');
                        collapsedCount++;
                    }
                });

                button.textContent = moreText;
                button.classList.remove('is-expanded');

                // Nothing left to reveal — the button is a dead end, so retire it
                // rather than let it sit there doing nothing on click.
                if (collapsedCount === 0) {
                    button.style.display = 'none';
                }
            }
        }

        toggleFilterValue(value, add) {
            if (!this.filters[this.source]) {
                this.filters[this.source] = [];
            }

            const index = this.filters[this.source].indexOf(value);

            if (add && index === -1) {
                this.filters[this.source].push(value);
            } else if (!add && index > -1) {
                this.filters[this.source].splice(index, 1);
            }

            if (this.filters[this.source].length === 0) {
                delete this.filters[this.source];
            }
        }

        setFilterValue(value) {
            this.filters[this.source] = [value];
        }

        async applyFilters() {
            if (this.isFiltering) return;
            
            // Debounce rapid filter changes
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.executeFilterRequest();
            }, 150);
        }

        async executeFilterRequest() {
            // BULLETPROOF FIX: Queue pattern - if request in progress, store pending state
            if (this.isFiltering) {
                this.pendingFilters = {...this.filters};
                return;
            }

            this.isFiltering = true;
            const requestId = ++this.lastRequestId;  // Track this request

            // Update URL without reload
            this.updateURL();

            // Cancel previous request if it exists
            if (this.controller) {
                this.controller.abort();
            }
            this.controller = new AbortController();

            // BULLETPROOF FIX: Register with global request manager to prevent race conditions
            window.StoredashRequestManager.register(this.controller, `filter:${this.source}`);

            // Client-side counts + optimistic product IDs: calculate BEFORE loading event
            let clientCountsApplied = false;
            let optimisticTotal = null;
            const descriptorMap = window.StoredashDescriptorMap;
            if (descriptorMap && descriptorMap.isReady()) {
                const context = this.getContext();
                let contextSet = null;
                // Build category context Set from descriptor map
                if (context.category_id) {
                    const body = document.body;
                    const catClasses = body.className.split(' ');
                    for (const cls of catClasses) {
                        const match = cls.match(/^term-(.+)/);
                        if (match && !cls.match(/^term-\d+$/)) {
                            contextSet = descriptorMap.getCategoryContext(match[1]);
                            if (contextSet) break;
                        }
                    }
                    // Fallback: build from category term ID body class
                    if (!contextSet && context.category_id) {
                        const catSource = descriptorMap._sources && descriptorMap._sources['product_cat'];
                        if (catSource) {
                            for (const val of catSource.values) {
                                if (val.productSet.size > 0) {
                                    // Try to match by checking if the body has the term ID class
                                    if (body.classList.contains('term-' + context.category_id)) {
                                        // We need the slug; search through values
                                    }
                                }
                            }
                        }
                    }
                }

                const clientCounts = descriptorMap.calculateCounts(this.filters, contextSet);
                if (clientCounts) {
                    this.updateFilterCounts(clientCounts);
                    clientCountsApplied = true;
                }

                // PERF: Compute optimistic product count for instant "N products found"
                const optimisticIds = descriptorMap.calculateProductIds(this.filters, contextSet);
                if (optimisticIds) {
                    optimisticTotal = optimisticIds.size;
                }
            }

            // Notify product grid to show skeleton loaders while AJAX is in-flight
            // Include optimistic total so result count widget can update immediately
            document.dispatchEvent(new CustomEvent('storedash:filters:loading', {
                bubbles: true,
                detail: optimisticTotal !== null ? { totalProducts: optimisticTotal } : undefined,
            }));

            // Build request params once (used for cache key + fetch body)
            const context = this.getContext();
            const widgetSettings = this.getProductGridSettings();
            const urlParams = new URLSearchParams(window.location.search);
            const currentOrderby = urlParams.get('orderby');
            const currentOrder = urlParams.get('order');
            const orderby = currentOrderby || widgetSettings.orderby || storedashFiltersData.orderby || 'menu_order';
            const order = currentOrder || widgetSettings.order || storedashFiltersData.order || 'ASC';

            const requestParams = {
                filters: this.filters,
                context: context,
                paged: 1,
                per_page: storedashFiltersData.perPage || 12,
                orderby: orderby,
                order: order,
                widget_settings: widgetSettings,
                render: 'html',
                skip_counts: clientCountsApplied,
            };

            // PERF: Check client-side AJAX result cache before hitting the server
            const cachedResponse = window.StoredashFilterCache.get(requestParams);
            if (cachedResponse) {
                // Check this request is still the latest before using cached data
                if (requestId !== this.lastRequestId) return;

                this.lastResponseData = cachedResponse;
                const data = cachedResponse;

                if (data.success) {
                    if (!clientCountsApplied && data.filter_counts && Object.keys(data.filter_counts).length > 0) {
                        this.updateFilterCounts(data.filter_counts);
                    }

                    const allFiltersCleared = Object.keys(this.filters).length === 0;
                    const eventData = {
                        filters: this.filters,
                        context: context,
                        totalProducts: data.total_products,
                        productIds: data.product_ids,
                        products_html: data.products_html,
                        products: data.products || [],
                        filter_counts: data.filter_counts || {},
                        widget_settings: data.widget_settings || widgetSettings,
                        i18n: data.i18n || {},
                        render_mode: data.render_mode || 'html',
                        cleared: allFiltersCleared,
                        totalPages: data.total_pages || 1,
                        currentPage: data.current_page || 1,
                    };
                    document.dispatchEvent(new CustomEvent('storedash:filters:changed', {
                        detail: eventData, bubbles: true, cancelable: true
                    }));
                }

                this.isFiltering = false;
                return;
            }

            // BULLETPROOF FIX: Retry logic with exponential backoff
            let retryCount = 0;
            const MAX_RETRIES = 2;
            let lastError = null;

            while (retryCount <= MAX_RETRIES) {
                try {
                    // Make AJAX request to our custom endpoint (uses fast custom tables!)
                    const response = await fetch(storedashFiltersData.restUrl + 'storedash/v1/ajax-filter', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-WP-Nonce': storedashFiltersData.nonce || '',
                        },
                        body: JSON.stringify(requestParams),
                        signal: this.controller.signal  // BULLETPROOF: Allow cancellation
                    });

                    // Check if this request is still the latest
                    if (requestId !== this.lastRequestId) {
                        return;
                    }

                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: Filter request failed`);
                    }

                    const data = await response.json();
                    this.lastResponseData = data;

                    // PERF: Store successful response in client-side cache
                    if (data.success) {
                        window.StoredashFilterCache.set(requestParams, data);
                    }

                    // Success! Break out of retry loop
                    lastError = null;
                    retryCount = MAX_RETRIES + 1; // Exit loop

                    if (data.success) {

                    // Update filter counts: use server counts only if client-side wasn't applied
                    if (!clientCountsApplied && data.filter_counts && Object.keys(data.filter_counts).length > 0) {
                        this.updateFilterCounts(data.filter_counts);
                    }

                    // Check if all filters are cleared
                    const allFiltersCleared = Object.keys(this.filters).length === 0;

                    // CRITICAL FIX: When filters cleared, load all products via AJAX (no reload!)
                    // Much smoother UX than page reload
                    if (allFiltersCleared) {
                        // Continue with normal AJAX flow - empty filters will return all products
                        // The API handles empty filters correctly by showing all products
                        // No need to reload page!
                    }

                    // Trigger NATIVE CustomEvent for product grid to handle its own rendering
                    // This is the critical fix - native event instead of jQuery!
                    const eventData = {
                        filters: this.filters,
                        context: context,
                        totalProducts: data.total_products,
                        productIds: data.product_ids,
                        products_html: data.products_html,
                        products: data.products || [],
                        filter_counts: data.filter_counts || {},
                        widget_settings: data.widget_settings || widgetSettings,
                        i18n: data.i18n || {},
                        render_mode: data.render_mode || 'html',
                        cleared: allFiltersCleared,
                        totalPages: data.total_pages || 1,
                        currentPage: data.current_page || 1,
                    };

                    const event = new CustomEvent('storedash:filters:changed', {
                        detail: eventData,
                        bubbles: true,
                        cancelable: true
                    });
                    
                    document.dispatchEvent(event);

                    // Check if product grid widget exists
                    const productGrids = document.querySelectorAll('.storedash-products-wrapper, .storedash-product-grid, .elementor-widget-storedash-product-grid');
                    
                    // If no product grid widget found, update manually (fallback)
                    if (productGrids.length === 0) {
                        this.updateProductGrid(
                            data.products_html,
                            data.products || [],
                            data.widget_settings || widgetSettings,
                            data.i18n || {}
                        );
                    }
                    }

                } catch (error) {
                    // AbortError: request was superseded by a newer one from the same source
                    // CRITICAL: Reset isFiltering so this instance can accept future requests
                    if (error.name === 'AbortError') {
                        this.isFiltering = false;
                        // PERF FIX: Retry guard — max 1 retry after AbortError to prevent infinite loop.
                        // Without this, rapid filter clicks could cause an unbounded retry chain.
                        if (this.pendingFilters && !this._abortRetrying) {
                            this._abortRetrying = true;
                            const pending = {...this.pendingFilters};
                            this.pendingFilters = null;
                            this.filters = pending;
                            setTimeout(() => {
                                this._abortRetrying = false;
                                this.executeFilterRequest();
                            }, 50);
                        }
                        return;
                    }

                    lastError = error;
                    retryCount++;

                    // If we have retries left, wait and try again
                    if (retryCount <= MAX_RETRIES) {
                        const backoffMs = 100 * Math.pow(2, retryCount - 1); // 100ms, 200ms, 400ms
                        await new Promise(resolve => setTimeout(resolve, backoffMs));
                    } else {
                        // BULLETPROOF: Stay on current state instead of reload
                        // User can retry by clicking filter again
                    }
                }
            }

            // If all retries failed and we have a last error, handle it
            if (lastError) {
                console.warn('Storedash: Filter request failed after retries', lastError.message);
            }

            // Cleanup after all retries complete
            this.isFiltering = false;

            // BULLETPROOF FIX: Check if filters changed while request was in progress
            if (this.pendingFilters) {
                const hasPendingChanges = JSON.stringify(this.pendingFilters) !== JSON.stringify(this.filters);

                if (hasPendingChanges) {
                    // Copy pending filters to current
                    this.filters = {...this.pendingFilters};
                    this.pendingFilters = null;
                    // Execute new request with updated filters
                    setTimeout(() => this.executeFilterRequest(), 50);
                } else {
                    this.pendingFilters = null;
                }
            }
        }

        /**
         * Update product grid with filtered results (fallback for standard WooCommerce)
         */
        updateProductGrid(productsHTML, productsData = [], widgetSettings = {}, i18n = {}) {
            const productsContainer = document.querySelector('.products, .woocommerce-loop-product, ul.products, .storedash-products-wrapper');
            
            if (productsContainer) {
                // Fade out, update, fade in for smooth transition
                productsContainer.style.transition = 'opacity 0.2s';
                productsContainer.style.opacity = '0.3';
                
                setTimeout(() => {
                    let finalHTML = productsHTML;

                    if (!finalHTML && typeof window.StoredashProductRenderer !== 'undefined') {
                        finalHTML = window.StoredashProductRenderer.renderProducts(productsData || [], widgetSettings || {}, i18n || {});
                    }

                    if (!finalHTML) {
                        finalHTML = '';
                    }

                    productsContainer.innerHTML = finalHTML;
                    productsContainer.style.opacity = '1';

                    const liveRegion = document.getElementById('storedash-filter-live');
                    if (liveRegion) {
                        const count = this.lastResponseData?.total_products ?? '';
                        liveRegion.textContent = count ? count + ' products found' : 'Products updated';
                    }

                    // Scroll to top of products (optional)
                    const offset = productsContainer.getBoundingClientRect().top + window.pageYOffset - 100;
                    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                    window.scrollTo({
                        top: offset,
                        behavior: prefersReducedMotion ? 'auto' : 'smooth'
                    });

                    // Reinitialize WooCommerce scripts if available
                    if (typeof window.wc_product_gallery !== 'undefined') {
                        const galleries = document.querySelectorAll('.woocommerce-product-gallery');
                        galleries.forEach(gallery => {
                            if (typeof gallery.wc_product_gallery === 'function') {
                                gallery.wc_product_gallery();
                            }
                        });
                    }

                    // Trigger add to cart AJAX binding
                    document.body.dispatchEvent(new CustomEvent('wc-add-to-cart-variation-init'));
                }, 200);
            }
        }

        /**
         * Update filter counts across all filter widgets
         * NEW: Smart visibility - gray out or hide options with 0 products
         */
        updateFilterCounts(filterCounts = {}, options = {}) {
            const isCleared = options.cleared === true;
            // PERF FIX: Collect all DOM mutations first, then apply in a single rAF
            // This prevents layout thrashing when updating 120+ filter items
            const mutations = [];

            // Update counts in all filter widgets on page
            const allFilters = document.querySelectorAll('.storedash-filter');

            allFilters.forEach(filterWidget => {
                const source = filterWidget.dataset.source;

                if (!source) {
                    return;
                }

                if (!filterCounts[source]) {
                    if (isCleared) {
                        mutations.push(() => {
                            this.restoreWidgetBaselineCounts(filterWidget);
                            const instance = filterWidget._storedashFilterInstance;
                            if (instance) {
                                instance.resetFilterItemVisibility();
                            }
                        });
                    }
                    return;
                }

                const counts = filterCounts[source];

                // Update dropdown options
                const dropdown = filterWidget.querySelector('.storedash-filter-dropdown');
                if (dropdown) {
                    Array.from(dropdown.options).forEach(option => {
                        const value = option.value;
                        if (value && counts[value]) {
                            const count = counts[value].count;
                            const baseText = option.textContent.replace(/\s*\(\d+\)\s*$/, '');
                            mutations.push(() => {
                                option.textContent = `${baseText} (${count})`;
                                option.disabled = count === 0;
                                option.style.opacity = count === 0 ? '0.5' : '1';
                            });
                        } else if (isCleared && option.dataset.storedashBaseText) {
                            mutations.push(() => {
                                option.textContent = option.dataset.storedashBaseText;
                                option.disabled = false;
                                option.style.opacity = '';
                            });
                        }
                    });
                }

                // Update each filter item (for checkbox, radio, button, swatch displays)
                const filterItems = filterWidget.querySelectorAll('.storedash-filter-item');

                // Determine hideEmpty per-widget: panel filters never hide, inline filters use their own setting
                const isInPanel = filterWidget.closest('.storedash-pf-panel__content') !== null;
                const shouldHideEmpty = isInPanel ? false : (filterWidget.dataset.hideEmpty === 'yes');

                filterItems.forEach(item => {
                    const input = item.querySelector('input, button, .storedash-filter-swatch');
                    const value = input?.value || input?.dataset.value;

                    if (!value) return;

                    const countBadge = item.querySelector('.storedash-filter-count');
                    const countData = counts[value];
                    if (!countData && isCleared) {
                        mutations.push(() => {
                            if (item.dataset.storedashBaseCountText && countBadge) {
                                countBadge.textContent = item.dataset.storedashBaseCountText;
                            }
                            item.classList.add('available');
                            item.classList.remove('disabled', 'hidden');
                            item.style.display = '';
                            item.style.opacity = '1';
                            item.style.pointerEvents = '';
                            item.title = '';
                            if (input) input.disabled = false;
                        });
                        return;
                    }
                    const count = countData ? countData.count : 0;

                    // SMART VISIBILITY: Handle zero-count items
                    if (count === 0) {
                        // Don't disable items that are currently checked — user must be able to uncheck
                        const isChecked = input?.checked || input?.classList?.contains('active');
                        if (isChecked) {
                            mutations.push(() => {
                                if (countBadge) countBadge.textContent = `(${count})`;
                                item.style.opacity = '0.7';
                            });
                            return;
                        }

                        mutations.push(() => {
                            if (countBadge) countBadge.textContent = `(${count})`;
                            item.classList.add('disabled');
                            item.classList.remove('available');

                            if (shouldHideEmpty) {
                                item.classList.add('hidden');
                                item.style.display = 'none';
                            } else {
                                item.classList.remove('hidden');
                                item.style.display = '';
                                item.style.opacity = '0.5';
                                item.style.pointerEvents = 'none';
                                item.title = 'No products available with current filters';
                                if (input) input.disabled = true;
                            }
                        });
                    } else {
                        mutations.push(() => {
                            if (countBadge) countBadge.textContent = `(${count})`;
                            item.classList.add('available');
                            item.classList.remove('disabled', 'hidden');
                            item.style.display = '';
                            item.style.opacity = '1';
                            item.style.pointerEvents = '';
                            item.title = '';
                            if (input) input.disabled = false;
                        });
                    }
                });

                // Consequences of hiding rather than graying out. Pushed after the
                // per-item mutations above so it observes their applied result — the
                // whole batch runs in order inside one animation frame.
                if (shouldHideEmpty) {
                    mutations.push(() => {
                        // A filter whose values have all disappeared should go with
                        // them, rather than leave a titled empty card. Mirrors what
                        // FiltersRenderer already does server-side on first render.
                        const isEmpty = filterWidget.querySelectorAll('.storedash-filter-item:not(.hidden)').length === 0;
                        filterWidget.classList.toggle('storedash-filter-empty', isEmpty);

                        // In Predefined Filters the card sits in a flex wrapper; hiding
                        // only the inner card would leave its gap behind in the row.
                        const wrapper = filterWidget.closest('.storedash-predefined-filter-item');
                        if (wrapper) {
                            wrapper.classList.toggle('storedash-filter-empty', isEmpty);
                        }

                        // "Show More" is meaningless once hiding has trimmed the list to
                        // within the collapse limit — but a "Show Less" must survive.
                        const showMore = filterWidget.querySelector('.storedash-filter-show-more');
                        if (showMore) {
                            const stillCollapsed = filterWidget.querySelectorAll('.storedash-filter-item.storedash-collapsed:not(.hidden)').length;
                            const isExpanded = showMore.classList.contains('is-expanded');
                            showMore.style.display = (!isExpanded && stillCollapsed === 0) ? 'none' : '';
                        }
                    });
                }
            });

            // Apply all mutations in a single animation frame
            requestAnimationFrame(() => {
                for (let i = 0; i < mutations.length; i++) {
                    mutations[i]();
                }

                // Reorder: move available items to top for filters with data-active-values-first
                allFilters.forEach(filterWidget => {
                    if (filterWidget.dataset.activeValuesFirst !== 'yes') return;

                    // Handle .storedash-filter-items containers (checkbox, radio, button, swatch, popover)
                    const itemContainers = filterWidget.querySelectorAll('.storedash-filter-items');
                    itemContainers.forEach(container => {
                        const items = Array.from(container.querySelectorAll(':scope > .storedash-filter-item'));
                        if (!items.length) return;

                        // Stable sort: available first, preserve relative order within each group
                        const sorted = items.slice().sort((a, b) => {
                            const aDisabled = a.classList.contains('disabled') ? 1 : 0;
                            const bDisabled = b.classList.contains('disabled') ? 1 : 0;
                            return aDisabled - bDisabled;
                        });

                        // Only reorder if order actually changed
                        let changed = false;
                        for (let i = 0; i < sorted.length; i++) {
                            if (sorted[i] !== items[i]) { changed = true; break; }
                        }
                        if (changed) {
                            sorted.forEach(item => container.appendChild(item));
                        }
                    });

                    // Handle dropdown options
                    const dropdown = filterWidget.querySelector('.storedash-filter-dropdown');
                    if (dropdown) {
                        const opts = Array.from(dropdown.options);
                        const sortedOpts = opts.slice().sort((a, b) => {
                            return (a.disabled ? 1 : 0) - (b.disabled ? 1 : 0);
                        });
                        let changed = false;
                        for (let i = 0; i < sortedOpts.length; i++) {
                            if (sortedOpts[i] !== opts[i]) { changed = true; break; }
                        }
                        if (changed) {
                            sortedOpts.forEach(opt => dropdown.appendChild(opt));
                        }
                    }
                });
            });
        }

        /**
         * Capture baseline count labels from initial DOM state.
         * Used as a fallback when clear events do not include fresh filter counts.
         */
        captureBaselineCounts() {
            const allFilters = document.querySelectorAll('.storedash-filter');
            allFilters.forEach(filterWidget => {
                this.captureWidgetBaselineCounts(filterWidget);
            });
        }

        captureWidgetBaselineCounts(filterWidget) {
            const dropdown = filterWidget.querySelector('.storedash-filter-dropdown');
            if (dropdown) {
                Array.from(dropdown.options).forEach(option => {
                    option.dataset.storedashBaseText = option.textContent;
                });
            }

            const filterItems = filterWidget.querySelectorAll('.storedash-filter-item');
            filterItems.forEach(item => {
                const countBadge = item.querySelector('.storedash-filter-count');
                if (countBadge) {
                    item.dataset.storedashBaseCountText = countBadge.textContent;
                }
            });
        }

        restoreWidgetBaselineCounts(filterWidget) {
            const dropdown = filterWidget.querySelector('.storedash-filter-dropdown');
            if (dropdown) {
                Array.from(dropdown.options).forEach(option => {
                    if (option.dataset.storedashBaseText) {
                        option.textContent = option.dataset.storedashBaseText;
                    }
                    option.disabled = false;
                    option.style.opacity = '';
                });
            }

            const filterItems = filterWidget.querySelectorAll('.storedash-filter-item');
            filterItems.forEach(item => {
                const countBadge = item.querySelector('.storedash-filter-count');
                if (countBadge && item.dataset.storedashBaseCountText) {
                    countBadge.textContent = item.dataset.storedashBaseCountText;
                }
            });
        }

        restoreBaselineCounts() {
            const allFilters = document.querySelectorAll('.storedash-filter');
            allFilters.forEach(filterWidget => {
                this.restoreWidgetBaselineCounts(filterWidget);
            });
        }

        async handleSearch(searchInput) {
            const term = searchInput.value;

            if (term.length < 2) {
                // Show all items
                const allItems = this.container.querySelectorAll('.storedash-filter-item');
                allItems.forEach(item => item.style.display = '');
                return;
            }

            try {
                const response = await fetch(
                    storedashFiltersData.restUrl + 'storedash/v1/search-filter-values' +
                    `?source=${this.source}&search=${encodeURIComponent(term)}`
                );

                const data = await response.json();

                if (data.success) {
                    // Hide all items first
                    const allItems = this.container.querySelectorAll('.storedash-filter-item');
                    allItems.forEach(item => item.style.display = 'none');

                    // Show matching items
                    data.values.forEach(value => {
                        const matchingInput = this.container.querySelector(`[data-value-id="${value.value_id}"]`);
                        if (matchingInput) {
                            const item = matchingInput.closest('.storedash-filter-item');
                            if (item) {
                                item.style.display = '';
                            }
                        }
                    });
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.warn('Storedash: Filter search failed', error.message);
                }
            }
        }

        updateActiveStates() {
            const selectedValues = this.filters[this.source] || [];
            const isSelected = (v) => selectedValues.includes(v);

            // Update checkboxes/radios
            const inputs = this.container.querySelectorAll('input[type="checkbox"], input[type="radio"]');
            inputs.forEach(input => {
                const selected = isSelected(input.value);
                input.checked = selected;
                const item = input.closest('.storedash-filter-item');
                if (item) {
                    item.classList.toggle('active', selected);
                }
            });

            // Update dropdown
            const dropdown = this.container.querySelector('.storedash-filter-dropdown');
            if (dropdown) {
                Array.from(dropdown.options).forEach(option => {
                    option.selected = isSelected(option.value);
                });
            }

            // Update buttons
            const buttons = this.container.querySelectorAll('.storedash-filter-button');
            buttons.forEach(button => {
                button.classList.toggle('active', isSelected(button.dataset.value));
            });

            // Update swatches
            const swatches = this.container.querySelectorAll('.storedash-filter-swatch');
            swatches.forEach(swatch => {
                swatch.classList.toggle('active', isSelected(swatch.dataset.value));
            });
        }

        /**
         * Reset visibility of all filter items to their default state.
         * Called when filters are cleared to undo hiding/disabling from updateFilterCounts().
         */
        resetFilterItemVisibility() {
            const filterItems = this.container.querySelectorAll('.storedash-filter-item');
            filterItems.forEach(item => {
                item.classList.remove('disabled', 'hidden');
                item.classList.add('available');
                item.style.display = '';
                item.style.opacity = '';
                item.style.pointerEvents = '';
                item.title = '';

                const input = item.querySelector('input, button, .storedash-filter-swatch');
                if (input) {
                    input.disabled = false;
                }
            });

            // Also reset dropdown options
            const dropdown = this.container.querySelector('.storedash-filter-dropdown');
            if (dropdown) {
                Array.from(dropdown.options).forEach(option => {
                    option.disabled = false;
                    option.style.opacity = '';
                });
            }
        }

        updateURL() {
            const params = new URLSearchParams(window.location.search);

            // Clear existing filter params for this source
            params.delete('filter_' + this.source);

            // Remove pagination params (filters reset pagination to page 1)
            params.delete('paged');
            params.delete('page');

            // Add new filter values if any
            if (this.filters[this.source] && this.filters[this.source].length > 0) {
                params.set('filter_' + this.source, this.filters[this.source].join(','));
            }

            // Build new URL respecting permalink structure
            let newUrl = window.location.pathname;
            const queryString = params.toString();
            
            if (queryString) {
                newUrl += '?' + queryString;
            }

            // Use History API for shareable URLs (no reload!)
            const state = {
                filters: this.filters,
                timestamp: Date.now(),
                source: this.source,
                storedashFilters: true, // Marker to identify our state
            };
            
            window.history.pushState(state, '', newUrl);
            
        }

        parseURL() {
            const params = new URLSearchParams(window.location.search);
            const filters = {};

            params.forEach((value, key) => {
                if (key.startsWith('filter_')) {
                    const source = key.replace('filter_', '');
                    filters[source] = value.split(',');
                }
            });

            return filters;
        }

        destroy() {
            if (this.debounceTimer) clearTimeout(this.debounceTimer);
            if (this.controller) this.controller.abort();

            // Remove container-scoped listeners
            if (this._boundHandlers.change) {
                this.container.removeEventListener('change', this._boundHandlers.change);
            }
            if (this._boundHandlers.click) {
                this.container.removeEventListener('click', this._boundHandlers.click);
            }
            if (this._boundHandlers.input) {
                this.container.removeEventListener('input', this._boundHandlers.input);
            }

            // Remove document-scoped listeners
            // Note: popoverClose and popoverEscape are now singletons — not per-instance
            if (this._boundHandlers.crossWidgetSync) {
                document.removeEventListener('storedash:filters:changed', this._boundHandlers.crossWidgetSync);
            }

            this._boundHandlers = {};

            if (this.container) {
                delete this.container._storedashFilterInstance;
            }
        }
    }

    /**
     * Initialize filters on Elementor frontend
     */
    function initElementorFilters() {
        
        if (typeof elementorFrontend !== 'undefined' && elementorFrontend.hooks) {
            // Hook for regular Filters widget
            elementorFrontend.hooks.addAction('frontend/element_ready/storedash-filters.default', function($scope) {
                // $scope might be jQuery or native - handle both
                const element = $scope instanceof Element ? $scope : $scope[0];
                const filterContainer = element.querySelector('.storedash-filter');
                if (filterContainer) {
                    if (filterContainer._storedashFilterInstance) {
                        filterContainer._storedashFilterInstance.destroy();
                    }
                    new StoredashFilters(filterContainer);
                }
            });
            
            // Hook for Predefined Filters widget - initialize all filters inside
            elementorFrontend.hooks.addAction('frontend/element_ready/storedash-predefined-filters.default', function($scope) {
                const element = $scope instanceof Element ? $scope : $scope[0];
                // Find all .storedash-filter containers inside the predefined filters widget
                const filterContainers = element.querySelectorAll('.storedash-filter');
                filterContainers.forEach(container => {
                    if (!container.dataset.initialized) {
                        if (container._storedashFilterInstance) {
                            container._storedashFilterInstance.destroy();
                        }
                        new StoredashFilters(container);
                        container.dataset.initialized = 'true';
                    }
                });
            });
        } else {
            // Elementor hooks not available - use fallback initialization
            const filterContainers = document.querySelectorAll('.elementor-widget-storedash-filters .storedash-filter, .elementor-widget-storedash-predefined-filters .storedash-filter');

            if (filterContainers.length > 0) {
                filterContainers.forEach((container, index) => {
                    if (!container.dataset.initialized) {
                        if (container._storedashFilterInstance) {
                            container._storedashFilterInstance.destroy();
                        }
                        new StoredashFilters(container);
                        container.dataset.initialized = 'true';
                    }
                });
            }
        }
    }

    /**
     * Initialize filters on regular pages (non-Elementor)
     */
    function initFilters() {
        const filterContainers = document.querySelectorAll('.storedash-filter');
        filterContainers.forEach(container => {
            if (!container.dataset.initialized) {
                if (container._storedashFilterInstance) {
                    container._storedashFilterInstance.destroy();
                }
                new StoredashFilters(container);
                container.dataset.initialized = 'true';
            }
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            if (typeof elementorFrontend === 'undefined') {
                initFilters();
            }
        });
    } else {
        // DOM already loaded
        if (typeof elementorFrontend === 'undefined') {
            initFilters();
        }
    }

    // Initialize Elementor hooks
    if (typeof elementorFrontend !== 'undefined') {
        initElementorFilters();
    } else {
        window.addEventListener('elementor/frontend/init', initElementorFilters);
    }

    // Expose class for external use
    window.Storedash = window.Storedash || {};
    window.Storedash.Filters = StoredashFilters;
    window.StoredashFilters = StoredashFilters; // backward compat

    // BULLETPROOF FIX: Handle browser back/forward buttons with AJAX (no reload!)
    // PERF FIX: Coordinator pattern ensures only ONE widget fires the AJAX request on popstate.
    // Without this, N filter widgets = N identical AJAX requests.
    window.addEventListener('popstate', (event) => {

        if (event.state && event.state.storedashFilters) {
            // FIX: If clear-filters.js already handled this (cleared: true), don't make duplicate request
            // clear-filters.js makes its own AJAX call, so we only need to update widget states
            const skipAjax = event.state.cleared === true && event.state.source === 'clear-filters-button';

            // Parse filters from URL
            const params = new URLSearchParams(window.location.search);
            const filters = {};

            params.forEach((value, key) => {
                if (key.startsWith('filter_')) {
                    const source = key.replace('filter_', '');
                    filters[source] = value.split(',');
                }
            });

            // PERF FIX: Only fire AJAX from the first widget instance
            let ajaxFired = false;

            // Update all filter widgets to match URL state
            document.querySelectorAll('.storedash-filter').forEach(filterWidget => {
                const instance = filterWidget._storedashFilterInstance;
                if (instance) {
                    instance.filters = filters;
                    instance.updateActiveStates();
                    if (!skipAjax && !ajaxFired) {
                        // First instance fires the AJAX request; others just sync UI
                        ajaxFired = true;
                        instance.executeFilterRequest();
                    } else if (Object.keys(filters).length === 0) {
                        // Navigated to cleared state — restore item visibility
                        instance.resetFilterItemVisibility();
                    }
                }
            });
        }
    });

})();
